import React from 'react';
import get from 'lodash.get';
import set from 'lodash.set';
import { cloneDeep, flowRight as compose } from 'lodash';
import moment from 'moment-timezone';
import omit from 'lodash.omit';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';
import { withApollo, graphql } from 'react-apollo';

import withRouting, { withRoutingPropType } from '../withRouting';
import BookingPropType from '../../constants/types/Booking';
import BookingPermission from '../../constants/enums/BookingPermission';
import BOOKING_STATUS from '../../constants/enums/BookingStatus';
import PAYMENT_METHODS from '../../constants/enums/PaymentMethod';

import BookingNoPermission from '../../components/BookingNoPermission';
import ErrorPage from '../../components/ErrorPage';
import Placeholder from '../../components/Placeholder';
import messages from './messages';

import mutationError from '../../lib/mutationError';
import { getUnique } from '../../lib/arrays';
import { parseDateRangeWithMoment } from '../../lib/RoomSchedules';
import Redirect from '../../components/Redirect';
import { checkBookingRoute } from '../../helpers/navigationHelpers';
import { convertUTCDateToPropertyTime, convertPropertyDateToUTCTime } from '../../helpers/timeZoneHelper';
import withAnalytics, { withAnalyticsPropType } from '../withAnalytics';

import {
  MUTATION_ADD_CREW_MEMBERS_TO_BOOKING_ROOM,
  MUTATION_CALCULATE_BOOKING_PRICE_FROM_FULL_BOOKING,
  MUTATION_CALCULATE_RATES_FOR_ROOM_SCHEDULE_EDIT,
  MUTATION_CALCULATE_RATES_FOR_ROOM_SCHEDULE_INITIAL,
  MUTATION_CANCEL_BOOKING,
  MUTATION_CHANGE_CHECKIN_CHECKOUT_DATE,
  MUTATION_EDIT_BOOKING_GUEST,
  MUTATION_INITIATE_BOOKING,
  MUTATION_MODIFY_SPECIAL_REQUEST,
  MUTATION_SET_COST_TRACKING_CODES_FOR_BOOKING,
  MUTATION_START_EDIT,
  MUTATION_SUBMIT_BOOKING,
  MUTATION_UPDATE_BOOKING_TRAVEL_REQUEST,
  QUERY_GET_BOOKING,
  QUERY_GET_IDENTITY_FOR_EMAIL,
  QUERY_GET_ORGANIZATION_COST_TRACKING_CODES,
  QUERY_GET_USER_PAYMENT_CODES,
  QUERY_PROPERTY_ROOMS
} from './gql';
// enum
import PropertyAvailability from '../../constants/enums/PropertyAvailability';
import { roomSchedulesAnalyticsFunction, omitDeep } from '../../helpers/bookingHelper';
import BOOKING_ROUTES from '../../constants/enums/BookingRoutes';
import BookingGuestDayState, { ReconciledPastBookingGuestDayState } from '../../constants/enums/BookingGuestDayState';
import BookingGuestDayStatus from '../../constants/enums/BookingGuestDayStatus';
import BookingCategory from '../../constants/enums/BookingCategory';
import ORGANIZATION_PERMISSIONS from '../../constants/enums/OrganizationPermission';
import { hasOrganizationPermission } from '../../helpers/permissionHelper';

export const BOOKING_PERMISSIONS = BookingPermission;

export const withBookingPropType = PropTypes.shape({
  startPropertyBookingFlow: PropTypes.func.isRequired,
  booking: BookingPropType.isRequired
});

const mapGuestToMutationInput = values => {
  const getValue = (path, alt = null) => get(values, path, alt);
  const guestInfo = {
    assigned: getValue('assigned', true),
    id: parseInt(getValue('id'), 10),
    firstName: getValue('firstName'),
    lastName: getValue('lastName'),
    company: getValue('company'),
    position: getValue('position'),
    referenceNumber: getValue('referenceNumber'),
    supervisor: getValue('supervisor', false),
    phone: getValue('phone'),
    emailAddress: getValue('emailAddress'),
    roomId: getValue('room.id'),
    nightShift: getValue('nightShift', false),
    comments: getValue('comments'),
    roomIndex: getValue('roomIndex')
  };

  return guestInfo;
};

const withBooking = options => BaseComponent => {
  const requireBooking = get(options, 'requireBooking', false);
  const allowEmptyPermissions = get(options, 'allowEmptyPermissions', true);
  const requireAllPermissions = get(options, 'requireAllPermissions') || [];
  const validateBookingState = get(options, 'validateBookingState', () => true);
  const errorOnServerErrors = get(options, 'errorOnServerErrors', false);
  const errorOnInvalidRoomTypes = get(options, 'errorOnInvalidRoomTypes', false);
  const isEditingBooking = get(options, 'editBooking', false);

  class ComponentWithBooking extends React.Component {
    static displayName = `withBooking(${BaseComponent.displayName || BaseComponent.name})`;

    static propTypes = {
      intl: intlShape.isRequired,
      router: withRoutingPropType.isRequired,
      analytics: withAnalyticsPropType.isRequired
    };

    constructor(props) {
      super(props);
      this.asyncSetState = this.asyncSetState.bind(this);
      this.formattedDates = this.formattedDates.bind(this);
      this.convertToMoment = this.convertToMoment.bind(this);
      this.startPropertyBookingFlow = this.startPropertyBookingFlow.bind(this);
      this.initiateBooking = this.initiateBooking.bind(this);
      this.calculateBookingPriceFromFullBooking = this.calculateBookingPriceFromFullBooking.bind(this);
      this.calculateRoomScheduleBookingEditRates = this.calculateRoomScheduleBookingEditRates.bind(this);
      this.calculateRoomScheduleBookingInitialRates = this.calculateRoomScheduleBookingInitialRates.bind(this);
      this.cancelBooking = this.cancelBooking.bind(this);
      this.startEditBooking = this.startEditBooking.bind(this);
      this.submitBooking = this.submitBooking.bind(this);
      this.updateBookingTravelRequest = this.updateBookingTravelRequest.bind(this);
      this.updateSpecialRequests = this.updateSpecialRequests.bind(this);
      this.editGuestInfo = this.editGuestInfo.bind(this);
      this.setCostTrackingCodes = this.setCostTrackingCodes.bind(this);
      this.navigateToBookingScreen = this.navigateToBookingScreen.bind(this);
      this.gotoRoomAssignments = this.gotoRoomAssignments.bind(this);
      this.gotoCostTrackingCodes = this.gotoCostTrackingCodes.bind(this);
      this.gotoRoomSchedule = this.gotoRoomSchedule.bind(this);
      this.gotoPayment = this.gotoPayment.bind(this);
      this.gotoSummary = this.gotoSummary.bind(this);
      this.gotoConfirm = this.gotoConfirm.bind(this);
      this.handleMutationError = this.handleMutationError.bind(this);
      this.changeCheckinCheckoutDate = this.changeCheckinCheckoutDate.bind(this);
      this.getBookingPaymentStatus = this.getBookingPaymentStatus.bind(this);
      this.createEditedBooking = this.createEditedBooking.bind(this);
      this.getBookingObject = this.getBookingObject.bind(this);
      this.setBookingObject = this.setBookingObject.bind(this);
      this.modifyBookingObject = this.modifyBookingObject.bind(this);
      this.updateEditedBookingSummary = this.updateEditedBookingSummary.bind(this);
      this.recalculateEditedBookingPrice = this.recalculateEditedBookingPrice.bind(this);
      this.getBookingStatus = this.getBookingStatus.bind(this);

      this.state = { error: null };
    }

    componentDidMount() {
      this.mounted = true;
    }

    componentWillUnmount() {
      this.mounted = false;
    }

    createEditedBooking = bookingArg => {
      const { booking: bookingStored, paymentCodes, analytics } = this.props;
      const unCleanedBookingCopy = cloneDeep(bookingStored ?? bookingArg);
      const bookingCopy = omitDeep(unCleanedBookingCopy, '__typename');

      // sometimes we don't have direct access to a Payment ID on a booking
      // this could happen if you are supervisor. you should still the booking Payment ID
      // even thought you don't have direct access to that code.
      const sourceCodes = paymentCodes || [];
      const bookingPaymentCode = get(bookingCopy, 'paymentCode', undefined);
      const combinedPaymentCodes = getUnique([...sourceCodes, bookingPaymentCode], 'id');
      const allGuestDays = [];
      const saveCTCs = [];
      const { checkin, checkout } = bookingCopy;
      const allDaysForCTCs = bookingCopy.guests[0].guestDays.flatMap(day => day);
      const haveCTCs = allDaysForCTCs.some(el => el.costTrackingCodeId);
      bookingCopy.guests.forEach(guest => {
        guest.guestDays = guest.guestDays.map(day => {
          day.bookingGuestId = guest.id;
          day.outboundRate = day.outboundRate.value;
          day.taxes = day.taxes.value;
          day.fees = day.fees.value;
          delete day?.costTrackingCode;
          allGuestDays.push(day);
          return day;
        });
      });

      const dateRange = parseDateRangeWithMoment([checkin, checkout]);

      dateRange.forEach(date => {
        const arrayOfDaysFromEachGuest = [];
        let costTrackingCodeId = '';
        bookingCopy.guests.forEach(guest => {
          const foundDay = guest.guestDays.find(el => el.date === date);
          if (foundDay && foundDay.status !== BookingGuestDayStatus.Vacant) {
            arrayOfDaysFromEachGuest.push(foundDay);
            ({ costTrackingCodeId } = foundDay);
          }
        });
        if (arrayOfDaysFromEachGuest.length > 0) {
          saveCTCs.push({ date, costTrackingCodeId });
        }
      });

      this.setBookingObject({
        booking: {
          ...bookingCopy,
          summary: this.buildSummary(),
          formattedDateRange: this.formattedDates(),
          totalNumberOfRooms: this.totalNumberOfRooms(),
          confirmedDates: this.getConfirmedDates(),
          confirmedPastDates: this.getConfirmedDates().filter(date => {
            const today = this.convertToMoment();
            const dateMoment = this.convertToMoment(date);

            return dateMoment.isBefore(today, 'day');
          }),
          guestSummaryByRoom: this.guestSummaryByRoom({})
        },
        policies: this.buildBookingFlowPolicies(),
        paymentCodes: combinedPaymentCodes
      });
      roomSchedulesAnalyticsFunction({
        booking: bookingCopy,
        sectionName: 'Property Details',
        bookingStartTime: `${Date.now()}`,
        eventName: 'createEditedBooking',
        analytics,
        initialFlow: false
      });
      this.modifyBookingObject('originalCheckInCheckOut', {
        checkin: bookingCopy.checkin,
        checkout: bookingCopy.checkout
      });
      this.modifyBookingObject('earliestCheckinLatestCheckout', {
        checkin: bookingCopy.checkin,
        checkout: bookingCopy.checkout
      });
      this.modifyBookingObject('originalGuestDays', allGuestDays);

      if (haveCTCs) {
        this.modifyBookingObject('saveCTCs', saveCTCs);
      }

      this.updateEditedBookingSummary();
    };

    convertToMoment(date) {
      const { booking = {} } = this.props;
      const { property } = booking;
      const { timeZone: propertyTimeZone } = property || {};

      return date ? moment.tz(date, propertyTimeZone) : moment.tz(propertyTimeZone);
    }

    getBookingObject = () => {
      const { booking } = this.props;
      return JSON.parse(sessionStorage.getItem('bookingObject')) ?? { booking };
    };

    setBookingObject = booking => {
      sessionStorage.setItem('bookingObject', JSON.stringify(booking));
    };

    updateNewBookingSummary = () => {
      const newBooking = this.getBookingObject();
      const { booking } = newBooking;
      newBooking.booking = this.generateSummaries(booking);

      this.setBookingObject(newBooking);
    };

    modifyBookingObject = (path, value) => {
      const editedBooking = this.getBookingObject();
      set(editedBooking, path, value);
      this.setBookingObject(editedBooking);
    };

    updateEditedBookingSummary = () => {
      const editedBooking = this.getBookingObject();
      const { booking } = editedBooking;
      const { estimatedTotal } = booking;
      const { symbol: currencySymbol, name: currencyName } = estimatedTotal;
      const roomSummary = [];

      booking.summary.guestCount = 0;

      booking.guests
        .filter(g => !g.removed)
        .forEach(guest => {
          booking.summary.guestCount += 1;

          // get booked days for guest
          let bookedDays = guest.guestDays.filter(
            day => day.status === BookingGuestDayStatus.Booked && day.state !== BookingGuestDayState.ReconciledCancelled
          );
          bookedDays = bookedDays.sort();
          const totalNightsForGuest = bookedDays.length;

          if (roomSummary[guest.room.id]) {
            roomSummary[guest.room.id] = {
              ...roomSummary[guest.room.id],
              numberOfNights: roomSummary[guest.room.id].numberOfNights + totalNightsForGuest
            };
            if (!roomSummary[guest.room.id].roomIndices.includes(guest.roomIndex)) {
              roomSummary[guest.room.id].numberOfRooms += 1;
              roomSummary[guest.room.id].roomIndices.push(guest.roomIndex);
            }
          } else {
            roomSummary[guest.room.id] = {
              id: guest.room.id,
              dates: [],
              roomName: guest.room.name,
              numberOfNights: totalNightsForGuest,
              numberOfRooms: 1,
              roomIndices: [guest.roomIndex]
            };
          }
          bookedDays.forEach(day => {
            const { dates } = roomSummary[guest.room.id];
            const foundDate = dates.find(d => d.date === day.date);
            const bookedRateValue = day.outboundRate;

            if (foundDate) {
              const { roomIndices } = foundDate;
              if (!roomIndices.includes(guest.roomIndex)) {
                foundDate.breakdown.bookedRooms += 1;
                foundDate.breakdown.totalRooms += 1;
                if (day?.outboundRate) {
                  foundDate.breakdown.bookedRate.totalDayAmount += bookedRateValue;
                }
                if (day?.outboundRate && bookedRateValue !== foundDate.breakdown.bookedRate.value) {
                  foundDate.breakdown.bookedRate.allRatesForThisDate.push(bookedRateValue);
                  if (foundDate.breakdown.bookedRate.allRatesForThisDate.length > 1) {
                    const uniqRates = [...new Set(foundDate.breakdown.bookedRate.allRatesForThisDate)];
                    foundDate.breakdown.bookedRate.allRatesForThisDate = uniqRates;
                    foundDate.breakdown.bookedRate.formatted = `${currencySymbol}${String(
                      Math.min(...uniqRates).toFixed(2)
                    )} - ${currencySymbol}${String(Math.max(...uniqRates).toFixed(2))}`;
                  }
                }
                roomIndices.push(guest.roomIndex);
              }
            } else {
              const formattedValue = bookedRateValue
                ? `${currencySymbol}${bookedRateValue.toFixed(2)}`
                : `${currencySymbol}${bookedRateValue}`;

              dates.push({
                date: day.date,
                breakdown: {
                  bookedRooms: 1,
                  totalRooms: 1,
                  bookedRate: {
                    value: bookedRateValue,
                    formatted: formattedValue,
                    name: currencyName,
                    allRatesForThisDate: [bookedRateValue],
                    totalDayAmount: bookedRateValue
                  }
                },
                roomIndices: [guest.roomIndex]
              });
            }
          });
        });

      booking.summary.roomCount = 0;

      const summaryByRoomType = [];
      /* eslint-disable */
      for (const x in roomSummary) {
        roomSummary[x].roomTypePrice = roomSummary[x].dates.reduce(
          (a, date) => {
            const { breakdown } = date;
            a.name = breakdown.bookedRate.name;
            // recalculate roomTypePrice when more than 1 rate set for that day
            a.value +=
              breakdown.bookedRate.allRatesForThisDate.length > 1
                ? breakdown.bookedRate.totalDayAmount
                : breakdown.bookedRooms * breakdown.bookedRate.value;
            return a;
          },
          { value: 0, name: currencyName }
        );
        booking.summary.roomCount += roomSummary[x].numberOfRooms;
        roomSummary[x].roomTypePrice.formatted = `$${roomSummary[x].roomTypePrice.value.toFixed(2)} (${
          roomSummary[x].roomTypePrice.name
        })`;
        roomSummary[x].dates = roomSummary[x].dates.sort(
          (a, b) => this.convertToMoment(a.date).valueOf() - this.convertToMoment(b.date).valueOf()
        );
        summaryByRoomType.push(roomSummary[x]);
      }
      /* eslint-enable */
      booking.summary.estimatedTotal = editedBooking.estimatedTotal;
      booking.summaryByRoomType = summaryByRoomType;
      booking.totalNumberOfRooms = roomSummary.reduce((a, room) => a + room.numberOfRooms, 0);
      booking.roomCount = booking.summary.roomCount;
      booking.guestCount = booking.summary.guestCount;
      this.setBookingObject(editedBooking);
    };

    async recalculateEditedBookingPrice() {
      const localBooking = this.getBookingObject();
      const { guests } = localBooking.booking;

      const result = await this.calculateBookingPriceFromFullBooking();
      const { total, taxesAndFees, daysWithRates } = result.bookingPrice.data.calculateBookingPriceFromFullBooking;
      const { formatted, name } = total;
      const estimatedTotal = `${formatted} (${name})`;

      const modGuests = guests.map(guest => {
        guest.guestDays.forEach(guestDay => {
          // find based on roomIndex and date.
          const findDayRate = daysWithRates.find(
            day => day.roomIndex === guest.roomIndex && day.date === guestDay.date
          );
          if (findDayRate) {
            const { inboundRate, markup, taxes, fees, racRate } = findDayRate;
            guestDay.inboundRate = inboundRate;
            guestDay.markup = markup;
            guestDay.taxes = taxes;
            guestDay.fees = fees;
            guestDay.racRate = racRate;
          }
        });

        return guest;
      });

      this.modifyBookingObject('estimatedTotal', estimatedTotal);
      this.modifyBookingObject('booking.estimatedTotal', total);
      this.modifyBookingObject('booking.estimatedTaxesFees', taxesAndFees);
      this.modifyBookingObject('booking.guests', modGuests);
      this.updateEditedBookingSummary();
    }

    /**
     * This method takes all the inputs required to start a brand new booking
     * encodes the parameters and then redirects to the initiate booking page.
     * We do this so that if the user isn't authenticated at this moment, they will
     * be taken to the signin page with a redirect link back to the page that actually calls
     * the initiateBooking mutation.
     *
     * @memberof ComponentWithBooking
     */
    startPropertyBookingFlow = (
      propertyId,
      checkin,
      checkout,
      rooms,
      bookingBypassReasonId,
      propertyTimeZone,
      isBlockBooking
    ) => {
      const { router } = this.props;
      const { pushRouteAndScroll } = router;
      const bookingCategory = isBlockBooking ? BookingCategory.Block : BookingCategory.Standard;
      // QUESTION: do we need to add any input validation or error handling here?

      // encode the input params
      const variables = {
        propertyId,
        checkin,
        checkout,
        rooms,
        bookingBypassReasonId,
        propertyTimeZone,
        bookingCategory
      };
      const encodedVars = encodeURIComponent(JSON.stringify(variables));

      // redirect
      pushRouteAndScroll('InitiateBooking', { vars: encodedVars });
    };

    // function created to calculate room type price
    calculateRoomTypePrice(dates) {
      return dates.reduce(
        (acc, date) => {
          const { breakdown } = date;
          acc.name = breakdown.bookedRate.name;
          acc.value += breakdown.bookedRooms * breakdown.bookedRate.value;
          return acc;
        },
        { value: 0 }
      );
    }

    // function created to format room type price
    formatRoomTypePrice(roomTypePrice) {
      return {
        ...roomTypePrice,
        formatted: `$${roomTypePrice.value.toFixed(2)} (${roomTypePrice.name})`
      };
    }

    // function created to sort dates
    sortDatesByDate(dates, convertToMoment) {
      return dates.sort((a, b) => convertToMoment(a.date).valueOf() - convertToMoment(b.date).valueOf());
    }

    // function created to get optimized room summary
    optimizeRoomSummary(roomSummary, summaryByRoomType, booking) {
      Object.keys(roomSummary).forEach(key => {
        const room = roomSummary[key];
        const roomTypePrice = this.calculateRoomTypePrice(room.dates);

        booking.summary.roomCount += room.numberOfRooms;
        room.roomTypePrice = this.formatRoomTypePrice(roomTypePrice);

        room.dates = this.sortDatesByDate(room.dates, this.convertToMoment);
        summaryByRoomType.push(room);
      });
    }

    generateSummaries = booking => {
      const { estimatedTotal } = booking;
      const { symbol: currencySymbol, name: currencyName } = estimatedTotal;
      const roomSummary = {};

      booking.summary = this.buildSummary(booking);

      booking.summary.guestCount = 0;

      booking.guests
        .filter(g => !g.removed)
        .forEach(guest => {
          booking.summary.guestCount += 1;

          // get booked days for guest
          let bookedDays = guest.guestDays.filter(day => day.status === 'Booked');
          bookedDays = bookedDays.sort();
          const totalNightsForGuest = bookedDays.length;

          if (roomSummary[guest.room.id]) {
            roomSummary[guest.room.id] = {
              ...roomSummary[guest.room.id],
              numberOfNights: roomSummary[guest.room.id].numberOfNights + totalNightsForGuest
            };
            if (!roomSummary[guest.room.id].roomIndices.includes(guest.roomIndex)) {
              roomSummary[guest.room.id].numberOfRooms += 1;
              roomSummary[guest.room.id].roomIndices.push(guest.roomIndex);
            }
          } else {
            roomSummary[guest.room.id] = {
              id: guest.room.id,
              dates: [],
              roomName: guest.room.name,
              numberOfNights: totalNightsForGuest,
              numberOfRooms: 1,
              roomIndices: [guest.roomIndex]
            };
          }
          bookedDays.forEach(day => {
            const { dates } = roomSummary[guest.room.id];
            const foundDate = dates.find(d => d.date === day.date);
            const { outboundRate = 0 } = day;
            const bookedRateValue = outboundRate;

            if (foundDate) {
              const { roomIndices } = foundDate;
              if (!roomIndices.includes(guest.roomIndex)) {
                foundDate.breakdown.bookedRooms += 1;
                foundDate.breakdown.totalRooms += 1;
                roomIndices.push(guest.roomIndex);
              }
            } else {
              dates.push({
                date: day.date,
                breakdown: {
                  bookedRooms: 1,
                  totalRooms: 1,
                  bookedRate: {
                    value: bookedRateValue,
                    formatted: `${currencySymbol}${bookedRateValue.toFixed(2)}`,
                    name: currencyName
                  }
                },
                roomIndices: [guest.roomIndex]
              });
            }
          });
        });

      booking.summary.roomCount = 0;
      const summaryByRoomType = [];

      // Optimize the code to  calculateRoomTypePrice formatRoomTypePrice and sortDatesByDate
      this.optimizeRoomSummary(roomSummary, summaryByRoomType, booking);

      booking.summaryByRoomType = summaryByRoomType;
      booking.totalNumberOfRooms = summaryByRoomType.reduce((a, room) => a + room.numberOfRooms, 0);
      booking.roomCount = booking.summary.roomCount;
      booking.guestCount = booking.summary.guestCount;

      return booking;
    };

    /**
     * This method will parse the input vars from the 'startPropertyBookingFlow' method,
     * and call the service mutation. If a booking number is returned and the mutation
     * is successful then we redirect the user to the first screen of the booking flow.
     *
     * @memberof ComponentWithBooking
     */
    initiateBooking = async () => {
      const {
        intl,
        router,
        initiateBooking,
        paymentCodes,
        analytics,
        auth: { me }
      } = this.props;

      const errorMsg = intl.formatMessage(messages.initiateBookingError);
      const handleErrorState = (msg = errorMsg, errorCode = 500) =>
        this.asyncSetState({ error: { message: msg, code: errorCode } });

      // clear pre-existing errors
      this.asyncSetState({ error: null });

      // make sure there there are booking input params passed to the route
      const bookingInputVars = get(router, 'query.vars', null);
      if (!bookingInputVars) {
        handleErrorState();
        return;
      }

      // there are lots of possible errors here so handle that and set error state
      // if anything throws.
      let bookingNumber = null;
      try {
        const bookingInputObj = JSON.parse(decodeURIComponent(bookingInputVars));
        // next step is to ensure an int is passed in as the roomId
        bookingInputObj.rooms.forEach(room => {
          room.roomId = parseInt(room.roomId, 10);
        });

        // Convert dates before sending to initiate
        const { propertyTimeZone, ...initiateVars } = bookingInputObj;

        initiateVars.checkin = convertPropertyDateToUTCTime(bookingInputObj.checkin, propertyTimeZone);
        initiateVars.checkout = convertPropertyDateToUTCTime(bookingInputObj.checkout, propertyTimeZone);
        const response = await initiateBooking({ variables: { ...initiateVars } });
        const {
          data: {
            initiateBooking: { booking: bookingCopy }
          }
        } = response;
        const policies = this.buildBookingFlowPolicies(bookingCopy);

        bookingNumber = get(response, 'data.initiateBooking.booking.bookingNumber');
        const allGuestDays = [];

        bookingCopy.checkin = convertUTCDateToPropertyTime({
          dateInUTC: bookingCopy.checkin,
          propertyTimeZone: bookingCopy.property.timeZone
        });
        bookingCopy.checkout = convertUTCDateToPropertyTime({
          dateInUTC: bookingCopy.checkout,
          propertyTimeZone: bookingCopy.property.timeZone
        });

        const canUserAddGuests = policies.canSetCrewSchedule;
        // We want to fill in the guest object with the users details if they are a selfBooker or crew Booker since they cannot add guests and the guest obj is created during initiation
        const generateGuestObjForCrewRole = guest => {
          if (!canUserAddGuests) {
            guest.firstName = me?.firstName;
            guest.lastName = me?.lastName;
            guest.emailAddress = me.emailAddress;
            guest.assigned = true;
            guest.complete = true;
            guest.identityId = null;
            guest.company = me?.company;
            guest.phone = me?.phoneNumber;
            guest.position = me?.position;
          }
        };
        bookingCopy.guests.forEach(guest => {
          generateGuestObjForCrewRole(guest);
          guest.added = true;
          guest.dirty = true;
          guest.id = `added-${guest.id}`;
          guest.supervisor = false;
          guest.guestDays = guest.guestDays.map(day => {
            day.bookingGuestId = guest.id;
            day.outboundRate = day.outboundRate.value;
            day.taxes = day.taxes.value;
            day.fees = day.fees.value;
            day.dirty = true;
            day.date = convertUTCDateToPropertyTime({
              dateInUTC: day.date,
              propertyTimeZone: bookingCopy.property.timeZone
            });

            allGuestDays.push(day);
            return day;
          });
        });

        const updatedBookingWithSummaries = this.generateSummaries(bookingCopy);
        const sourceCodes = paymentCodes || [];
        const bookingPaymentCode = get(bookingCopy, 'paymentCode', undefined);
        const combinedPaymentCodes = getUnique([...sourceCodes, bookingPaymentCode], 'id');
        this.setBookingObject({
          booking: {
            ...updatedBookingWithSummaries,
            formattedDateRange: this.formattedDates(updatedBookingWithSummaries),
            confirmedDates: this.getConfirmedDates(),
            confirmedPastDates: this.getConfirmedDates().filter(date => {
              const today = this.convertToMoment();
              const dateMoment = this.convertToMoment(date);

              return dateMoment.isBefore(today, 'day');
            }),
            guestSummaryByRoom: this.guestSummaryByRoom({ bookingCopy })
          },
          policies,
          paymentCodes: combinedPaymentCodes,
          initialFlow: true
        });
        roomSchedulesAnalyticsFunction({
          booking: updatedBookingWithSummaries,
          sectionName: 'Property Details',
          bookingStartTime: `${Date.now()}`,
          eventName: 'initiateBooking',
          analytics
        });
        this.modifyBookingObject('originalCheckInCheckOut', {
          checkin: bookingCopy.checkin,
          checkout: bookingCopy.checkout
        });

        this.modifyBookingObject('earliestCheckinLatestCheckout', {
          checkin: bookingCopy.checkin,
          checkout: bookingCopy.checkout
        });

        this.modifyBookingObject('originalGuestDays', allGuestDays);
      } catch (error) {
        const { message, code } = mutationError(error);
        handleErrorState(message, code);
        return;
      }

      // even if the mutation doesn't throw, it might not be successful
      // so check that and handle.
      if (!bookingNumber) {
        handleErrorState();
        return;
      }
      // redirect to the guest information screen for the booking (replace route)
      this.navigateToBookingScreen(BOOKING_ROUTES.ROOMSCHEDULE, { bookingNumber }, true);
    };

    getIdentityForGuest = async emailAddress => {
      const { client } = this.props;
      const response = await client.query({
        query: QUERY_GET_IDENTITY_FOR_EMAIL,
        variables: { emailAddress }
      });

      return response.data.getIdentityForEmail;
    };

    calculateBookingPriceFromFullBooking = async () => {
      const { calculateBookingPriceFromFullBooking } = this.props;
      try {
        const { booking } = this.getBookingObject();
        const {
          property: { timeZone: propertyTimeZone }
        } = booking;
        const guests = booking.guests.filter(g => !g.removed);
        const input = {
          bookingId: booking.id,
          property: { id: booking.property.id },
          guests: guests
            .map(g => ({
              days: g.guestDays
                .filter(
                  ({ status, state }) =>
                    status === BookingGuestDayStatus.Booked && state !== BookingGuestDayState.ReconciledCancelled
                )
                .map(({ date, taxes, fees }) => {
                  const dateToUse = convertPropertyDateToUTCTime(date, propertyTimeZone);

                  return { date: dateToUse, taxes, fees };
                }),
              roomIndex: Number(g.roomIndex),
              room: {
                id: Number(g.room.id),
                roomType: {
                  id: Number(g.room.roomType.id)
                }
              }
            }))
            .filter(g => g.days.length)
        };

        const result = await calculateBookingPriceFromFullBooking({
          variables: { input }
        });

        const {
          data: {
            calculateBookingPriceFromFullBooking: { daysWithRates }
          }
        } = result;

        const convertedDaysWithRates = daysWithRates.map(day => {
          const convertedDate = convertUTCDateToPropertyTime({ dateInUTC: day.date, propertyTimeZone });
          return { ...day, date: convertedDate };
        });

        result.data.calculateBookingPriceFromFullBooking.daysWithRates = convertedDaysWithRates;

        return { bookingPrice: result };
      } catch (error) {
        return this.handleMutationError(mutationError(error));
      }
    };

    calculateRoomScheduleBookingEditRates = async () => {
      const { calculateRoomScheduleBookingEditRates } = this.props;
      try {
        const { booking } = this.getBookingObject();
        const { property } = booking;
        const propertyTimeZone = get(property, 'timeZone');
        const guests = booking.guests.filter(g => !g.removed);
        const input = {
          bookingId: booking.id,
          property: { id: booking.property.id },
          guests: guests.map(g => ({
            days: g.guestDays.map(({ date, status, taxes, fees }) => ({
              date: convertPropertyDateToUTCTime(date, propertyTimeZone),
              status,
              taxes,
              fees
            })),
            roomIndex: Number(g.roomIndex),
            room: {
              id: Number(g.room.id),
              roomType: {
                id: Number(g.room.roomType.id)
              }
            }
          }))
        };

        const result = await calculateRoomScheduleBookingEditRates({
          variables: { input }
        });

        const { booking: updatedBooking } = this.props;
        const { property: updatedProperty } = updatedBooking;
        const newRooms = updatedProperty.rooms;
        this.modifyBookingObject('booking.property.rooms', newRooms);

        const {
          data: {
            calculateRoomScheduleBookingEditRates: { daysWithRates }
          }
        } = result;
        const convertedDaysWithRates = daysWithRates.map(day => {
          const convertedDate = convertUTCDateToPropertyTime({ dateInUTC: day.date, propertyTimeZone });
          return { ...day, date: convertedDate };
        });

        result.data.calculateRoomScheduleBookingEditRates.daysWithRates = convertedDaysWithRates;
        return { bookingPrice: result };
      } catch (error) {
        return this.handleMutationError(mutationError(error));
      }
    };

    calculateRoomScheduleBookingInitialRates = async () => {
      const { calculateRoomScheduleBookingInitialRates } = this.props;
      try {
        const { booking } = this.getBookingObject();
        const { property } = booking;
        const propertyTimeZone = get(property, 'timeZone');
        const guests = booking.guests.filter(g => !g.removed);
        const input = {
          bookingId: booking.id,
          property: { id: booking.property.id },
          guests: guests.map(g => ({
            days: g.guestDays
              .filter(d => d.status !== BookingGuestDayStatus.Vacant)
              .map(({ date, status }) => ({ date: convertPropertyDateToUTCTime(date, propertyTimeZone), status })),
            roomIndex: Number(g.roomIndex),
            room: {
              id: Number(g.room.id),
              roomType: {
                id: Number(g.room.roomType.id)
              }
            }
          }))
        };

        const result = await calculateRoomScheduleBookingInitialRates({
          variables: { input }
        });

        const { booking: updatedBooking } = this.props;
        const { property: updatedProperty } = updatedBooking;
        const newRooms = updatedProperty.rooms;
        this.modifyBookingObject('booking.property.rooms', newRooms);

        const {
          data: {
            calculateRoomScheduleBookingInitialRates: { daysWithRates }
          }
        } = result;
        const convertedDaysWithRates = daysWithRates.map(day => {
          const convertedDate = convertUTCDateToPropertyTime({ dateInUTC: day.date, propertyTimeZone });
          return { ...day, date: convertedDate };
        });
        result.data.calculateRoomScheduleBookingInitialRates.daysWithRates = convertedDaysWithRates;

        return { bookingPrice: result };
      } catch (error) {
        return this.handleMutationError(mutationError(error));
      }
    };

    /**
     * Add an array of CrewMembers to a BookingRoom.
     *
     * @param {object} crewMembers: Array<Object> { id: Int, nightShift: Boolean, supervisor: Boolean }
     * @param {int} roomId: Int
     * @returns {string} mutationError: Object | booking: Booking
     * @memberof ComponentWithBooking
     */
    addCrewMembersToRoom = async (crewMembers, roomId, roomKey) => {
      const { addCrewMembersToBookingRoom } = this.props;
      const { booking } = this.getBookingObject();
      const { id: bookingId, property } = booking;
      const roomIndex = roomKey ? parseInt(roomKey, 10) : null;

      const rooms = get(property, 'rooms') || [];
      const slug = get(property, 'slug');
      const connected = get(property, 'connected', false);
      const checkin = get(booking, 'checkin');
      const checkout = get(booking, 'checkout');

      try {
        const result = await addCrewMembersToBookingRoom({
          variables: { bookingId, crewMembers, roomId, roomIndex },
          refetchQueries: [
            'QUERY_GET_BOOKING',
            'QUERY_GET_CREW_MEMBERS',
            {
              query: QUERY_PROPERTY_ROOMS,
              variables: {
                slug,
                dates: {
                  checkin,
                  checkout
                },
                skip: !checkin || !checkout || rooms.length === 0 || !connected || false
              }
            }
          ]
        });
        return { booking: result };
      } catch (error) {
        return this.handleMutationError(mutationError(error));
      }
    };

    editGuestInfo = async (guest, sendNotifications = false, sendGDSUpdate = false) => {
      const { editBookingGuest } = this.props;
      const { booking } = this.getBookingObject();
      const { id: bookingId, bookingNumber, property, guests } = booking;
      const guestInfo = mapGuestToMutationInput(guest);
      const rooms = get(property, 'rooms') || [];
      const currentGuest = guests.find(thisGuest => get(thisGuest, 'id') === get(guest, 'id'));
      const updatedRoom = rooms.find(room => get(room, 'id') === get(guest, 'room.id'));
      const isNewRoom = get(currentGuest, 'room.id') !== get(updatedRoom, 'id');
      const slug = get(property, 'slug');
      const connected = get(property, 'connected', false);
      const checkin = get(booking, 'checkin');
      const checkout = get(booking, 'checkout');
      try {
        const optimisticResponse = {
          __typename: 'Mutation',
          editGuestForBooking: {
            __typename: 'BookingGuestResponse',
            message: 'Guest successfully edited.',
            permissions: get(booking, 'permissions') || [],
            guest: {
              __typename: 'BookingGuest',
              ...guestInfo,
              room: { ...updatedRoom },
              guestDays: guestInfo.guestDays || [],
              identityId: null,
              complete: true
            }
          }
        };

        const update = (proxy, { data: { editGuestForBooking: updateResponse } }) => {
          const { guest: guestReponse, permissions } = updateResponse;
          const data = proxy.readQuery({
            query: QUERY_GET_BOOKING,
            variables: { bookingNumber, draftEdit: isEditingBooking }
          });
          const allGuestsComplete = data.getBookingByBookingNumber.guests.every(theGuest => theGuest.complete);
          const oldGuestIndex = data.getBookingByBookingNumber.guests.findIndex(
            theGuest => get(theGuest, 'id') === get(guestReponse, 'id')
          );

          if (oldGuestIndex) {
            data.getBookingByBookingNumber.guests[oldGuestIndex] = { ...guestReponse };
          }

          // If all the guests are completed in the response, assume we can have the submit payment permission
          // Is this dangerous? It may erroneously give someone this permission temporarily
          // TODO: Remove this once the batch editGuest mutation is in because then we should be able to expect
          // accurrate permissions back.
          data.getBookingByBookingNumber.permissions = [
            ...permissions,
            ...(allGuestsComplete ? [BookingPermission.SUBMIT_PAYMENT] : [])
          ];
          proxy.writeQuery({
            query: QUERY_GET_BOOKING,
            variables: { bookingNumber, draftEdit: isEditingBooking },
            data
          });
        };

        const result = await editBookingGuest({
          variables: { bookingId, guestInfo, sendNotifications, sendGDSUpdate },
          update,
          optimisticResponse,
          awaitRefetchQueries: true,
          refetchQueries: isNewRoom
            ? [
                'QUERY_GET_BOOKING',
                'QUERY_GET_CREW_MEMBERS',
                {
                  query: QUERY_PROPERTY_ROOMS,
                  variables: {
                    slug,
                    dates: {
                      checkin,
                      checkout
                    },
                    skip: !checkin || !checkout || rooms.length === 0 || !connected || false
                  }
                }
              ]
            : ['QUERY_GET_BOOKING', 'QUERY_GET_CREW_MEMBERS']
        });
        return { booking: result };
      } catch (error) {
        return this.handleMutationError(mutationError(error));
      }
    };

    setCostTrackingCodes = async costTrackingCodes => {
      const { setCostTrackingCodesForBooking, booking } = this.props;
      const { id: bookingId } = booking;

      try {
        const result = await setCostTrackingCodesForBooking({
          variables: { bookingId: parseInt(bookingId, 10), costTrackingCodes },
          refetchQueries: ['QUERY_GET_BOOKING']
        });
        return { booking: result };
      } catch (error) {
        return this.handleMutationError(mutationError(error));
      }
    };

    updateSpecialRequests = async specialRequests => {
      const { updateSpecialRequests, booking } = this.props;
      const { id } = booking;
      const bookingId = parseInt(id, 10);

      try {
        const result = await updateSpecialRequests({
          variables: { bookingId, specialRequests },
          refetchQueries: ['QUERY_GET_BOOKING']
        });
        return { booking: result };
      } catch (error) {
        return this.handleMutationError(mutationError(error));
      }
    };

    submitBooking = async (booking, isInitialFlow) => {
      const { submitBooking, booking: bookingFromProps } = this.props;
      const { property: propertyFromProps } = bookingFromProps;
      const { timeZone: propertyTimeZone } = propertyFromProps;

      if (booking.saveCTCs) {
        booking.saveCTCs = booking.saveCTCs.map(ctcDate => {
          const convertedCtcDate = convertPropertyDateToUTCTime(ctcDate.date, propertyTimeZone);
          return { ...ctcDate, date: convertedCtcDate };
        });
      }

      booking.guests.forEach(guest => {
        guest.guestDays = guest.guestDays.map(day => {
          const convertedDate = convertPropertyDateToUTCTime(day.date, propertyTimeZone);
          return { ...day, date: convertedDate };
        });
      });

      const {
        saveCTCs,
        paymentVars,
        datesUpdated = false,
        id,
        bookingFeeEvent = null,
        preferences = { admin: true, crew: true, supervisor: true },
        specialRequests = '',
        guests,
        bookingNumber,
        bookingOrigin,
        bookingStatus,
        checkin,
        checkout,
        connected,
        property
      } = booking;

      try {
        const result = await submitBooking({
          variables: {
            booking: {
              saveCTCs,
              paymentVars,
              datesUpdated,
              id,
              bookingFeeEvent,
              preferences,
              specialRequests,
              property,
              guests,
              bookingNumber,
              bookingOrigin,
              bookingStatus,
              checkin,
              checkout,
              connected
            },
            isInitialFlow
          },
          refetchQueries: ['QUERY_GET_BOOKING'],
          awaitRefetchQueries: true
        });
        return { booking: result };
      } catch (error) {
        return this.handleMutationError(mutationError(error));
      }
    };

    updateBookingTravelRequest = async (travelRequestType = 'None') => {
      const { updateBookingTravelRequest, booking } = this.props;
      const { id } = booking;
      const bookingId = parseInt(id, 10);

      try {
        const result = await updateBookingTravelRequest({
          variables: { bookingId, travelRequestType },
          refetchQueries: ['QUERY_GET_BOOKING']
        });
        return { booking: result };
      } catch (error) {
        return this.handleMutationError(mutationError(error));
      }
    };

    cancelBooking = async (preferences, bookingFeeEvent) => {
      const { cancelBooking, booking } = this.props;
      const { id } = booking;
      const bookingId = parseInt(id, 10);

      try {
        const result = await cancelBooking({
          variables: { bookingId, preferences, bookingFeeEvent }
        });
        return { booking: result };
      } catch (error) {
        return this.handleMutationError(mutationError(error));
      }
    };

    getBookingPaymentStatus() {
      const { intl, paymentCodes } = this.props;

      // eslint-disable-next-line no-prototype-builtins
      const { paymentVars, booking } = this.getBookingObject();

      const status = get(booking, 'bookingStatus');

      const paymentMethod = paymentVars ? paymentVars.method : get(booking, 'paymentMethod');

      const paymentCodeFromProps = get(booking, 'paymentCode');

      const buildPaymentTypeLabel = () => {
        if (paymentMethod === PAYMENT_METHODS.PaymentCode) {
          const paymentCodeId = paymentVars && paymentVars.paymentCodeId;
          const paymentCode = paymentCodeId ? paymentCodes.find(c => c.id === paymentCodeId) : paymentCodeFromProps;
          return intl.formatMessage(messages.paymentCodeLabel, {
            code: paymentCode?.code,
            organizationName: paymentCode?.organization.name
          });
        }
        const cardNickName = paymentVars
          ? paymentVars.cardNickName
          : get(booking, 'cardSource.cardNickname', 'UNKNOWN');
        const lastFourDigits = paymentVars
          ? paymentVars.lastFourDigits
          : get(booking, 'cardSource.lastFourDigits', 'XXXX');
        return intl.formatMessage(messages.creditCardLabel, { cardNickName, lastFourDigits });
      };

      const sharedValues = {
        label: buildPaymentTypeLabel(),
        linkText: intl.formatMessage(messages.paymentDisclaimerLink)
      };

      switch (status) {
        case BOOKING_STATUS.Pending: {
          return {
            ...sharedValues,
            disclaimer: intl.formatMessage(messages.paymentDisclaimerPending)
          };
        }
        case BOOKING_STATUS.DraftEdit: {
          return {
            ...sharedValues,
            disclaimer: intl.formatMessage(messages.paymentDisclaimerEditing)
          };
        }
        case BOOKING_STATUS.Confirmed: {
          return {
            ...sharedValues,
            disclaimer: intl.formatMessage(messages.paymentDisclaimerConfirmed)
          };
        }
        case BOOKING_STATUS.BookingInProgress: {
          return {
            ...sharedValues,
            disclaimer: intl.formatMessage(messages.paymentDisclaimerInProgress)
          };
        }
        case BOOKING_STATUS.Cancelled:
        case BOOKING_STATUS.CancelledAfterEdited: {
          return {
            ...sharedValues,
            disclaimer: intl.formatMessage(messages.paymentDisclaimerCancelled)
          };
        }
        case BOOKING_STATUS.Expired: {
          return {
            ...sharedValues,
            disclaimer: intl.formatMessage(messages.paymentDisclaimerExpired)
          };
        }
        case BOOKING_STATUS.Rejected:
        case BOOKING_STATUS.Reconciled:
        case BOOKING_STATUS.BookingComplete: {
          return {
            ...sharedValues,
            disclaimer: intl.formatMessage(messages.paymentDisclaimerComplete)
          };
        }
        default:
          return {
            ...sharedValues,
            disclaimer: intl.formatMessage(messages.paymentDisclaimerInProgress)
          };
      }
    }

    startEditBooking = async () => {
      const { startEditBooking, booking } = this.props;
      const { bookingNumber, property } = booking;

      const rooms = get(property, 'rooms') || [];
      const slug = get(property, 'slug');
      const connected = get(property, 'connected', false);
      const propertyTimeZone = get(property, 'timeZone');
      let checkin = get(booking, 'checkin');
      let checkout = get(booking, 'checkout');

      checkin = convertPropertyDateToUTCTime(checkin, propertyTimeZone);
      checkout = convertPropertyDateToUTCTime(checkout, propertyTimeZone);

      try {
        const result = await startEditBooking({
          variables: { bookingNumber },
          refetchQueries: [
            { query: QUERY_GET_BOOKING, variables: { bookingNumber, draftEdit: true } },
            {
              query: QUERY_PROPERTY_ROOMS,
              variables: {
                slug,
                dates: {
                  checkin,
                  checkout
                },
                skip: !checkin || !checkout || rooms.length === 0 || !connected || false
              }
            }
          ]
        });

        const {
          data: {
            startBookingEdit: { booking: bookingCopy }
          }
        } = result;
        bookingCopy.checkin = convertUTCDateToPropertyTime({
          dateInUTC: bookingCopy.checkin,
          propertyTimeZone: bookingCopy.property.timeZone
        });
        bookingCopy.checkout = convertUTCDateToPropertyTime({
          dateInUTC: bookingCopy.checkout,
          propertyTimeZone: bookingCopy.property.timeZone
        });
        result.data.startBookingEdit.booking = bookingCopy;

        return { booking: result };
      } catch (error) {
        return this.handleMutationError(mutationError(error));
      }
    };

    /**
     * Method returns a policy object using booking Permissions
     * that can be used to enable/disable features on the Booking flow pages
     * @memberof ComponentWithBooking
     */
    buildBookingFlowPolicies(bookingArgs) {
      let { booking } = this.props;
      if (bookingArgs) {
        booking = bookingArgs;
      }
      const permissions = get(booking, 'permissions') || [];

      const base = {
        canAddGuest: permissions.includes(BookingPermission.ADD_GUEST),
        canRemoveGuest: permissions.includes(BookingPermission.REMOVE_GUEST),
        canEditGuestRoom: permissions.includes(BookingPermission.EDIT_GUEST_CHANGE_ROOM),
        canEditGuestInformation: permissions.includes(BookingPermission.EDIT_GUEST_INFORMATION),
        canEditRoomAssignments: permissions.includes(BookingPermission.EDIT_GUEST_INFORMATION),
        canSelectBookingDates: permissions.includes(BookingPermission.SELECT_BOOKING_DATES),
        canSetCrewSchedule: permissions.includes(BookingPermission.SET_CREW_SCHEDULE),
        canSetGuestSchedule: permissions.includes(BookingPermission.SET_GUEST_SCHEDULE),
        canSubmitPayment: permissions.includes(BookingPermission.SUBMIT_PAYMENT),
        canFinalizeBooking: permissions.includes(BookingPermission.FINALIZE_BOOKING),
        canCancelBooking: permissions.includes(BookingPermission.CANCEL_BOOKING),
        canSetAnyDate: permissions.includes(BookingPermission.ADMIN_NO_DATE_RESTRICTIONS),
        canSetGuestUnknown: permissions.includes(BookingPermission.SET_GUEST_UNKNOWN)
      };

      return {
        ...base,
        canAccessRoomAssignments:
          base.canAddGuest ||
          base.canRemoveGuest ||
          base.canEditGuestRoom ||
          base.canEditGuestInformation ||
          base.canEditRoomAssignments,
        canAccessSchedule:
          base.canSubmitPayment && (base.canSelectBookingDates || base.canSetGuestSchedule || base.canSetCrewSchedule)
      };
    }

    navigateToBookingScreen(screen, params = {}, replaceRoute = false) {
      // this requires the page to compose the withAuth component before the withBooking component.
      const { router, auth } = this.props;
      const { replaceRouteAndScroll, pushRouteAndScroll } = router;

      // const bookingSlug = get(booking, 'organization.slug', false);
      const organizationSlug = get(auth, 'me.activeOrganization.slug');

      const routingArgs = [screen, { ...params, organizationSlug }];

      if (replaceRoute) {
        replaceRouteAndScroll(...routingArgs);
      } else {
        pushRouteAndScroll(...routingArgs);
      }
    }

    gotoRoomAssignments(e, edit = false) {
      if (e) {
        e.preventDefault();
      }
      const bookingNumber = get(this.props, 'booking.bookingNumber');
      this.navigateToBookingScreen(edit ? BOOKING_ROUTES.EDITASSIGNMENT : BOOKING_ROUTES.ASSIGNMENT, {
        bookingNumber
      });
    }

    gotoCostTrackingCodes(e, edit = false) {
      if (e) {
        e.preventDefault();
      }
      const bookingNumber = get(this.props, 'booking.bookingNumber');
      this.navigateToBookingScreen(
        edit ? BOOKING_ROUTES.EDIT_COST_TRACKING_CODES : BOOKING_ROUTES.COST_TRACKING_CODES,
        { bookingNumber }
      );
    }

    gotoRoomSchedule(e, edit = false) {
      if (e) {
        e.preventDefault();
      }
      const bookingNumber = get(this.props, 'booking.bookingNumber');
      if (!bookingNumber) {
        return;
      }
      this.navigateToBookingScreen(edit ? BOOKING_ROUTES.EDITROOMSCHEDULE : BOOKING_ROUTES.ROOMSCHEDULE, {
        bookingNumber
      });
    }

    gotoPayment(e, edit = false) {
      if (e) {
        e.preventDefault();
      }
      const bookingNumber = get(this.props, 'booking.bookingNumber');
      if (!bookingNumber) {
        return;
      }
      this.navigateToBookingScreen(edit ? BOOKING_ROUTES.EDITPAYMENT : BOOKING_ROUTES.PAYMENT, { bookingNumber });
    }

    // type is one of assignment, schedule, payment
    gotoSummary(e, edit = false, type = 'assignments') {
      if (e) {
        e.preventDefault();
      }
      const bookingNumber = get(this.props, 'booking.bookingNumber');
      if (!bookingNumber) {
        return;
      }
      if (!edit) {
        this.navigateToBookingScreen(BOOKING_ROUTES.SUMMARY, { bookingNumber });
      } else {
        this.navigateToBookingScreen(BOOKING_ROUTES.EDITSUMMARY, { bookingNumber, type });
      }
    }

    gotoConfirm(e) {
      if (e) {
        e.preventDefault();
      }
      const bookingNumber = get(this.props, 'booking.bookingNumber');
      if (!bookingNumber) {
        return;
      }
      this.navigateToBookingScreen(BOOKING_ROUTES.CONFIRM, { bookingNumber });
    }

    /**
     * Because we are using async methods a lot, we need to check to see if the component is still
     * mounted when we call setState to avoid the
     * react forceupdate warnings.
     *
     * @param {*} state
     * @memberof ComponentWithBooking
     */
    asyncSetState(state) {
      if (this.mounted) {
        this.setState(state);
      }
    }

    // Function that formats the checkin and checkout dates to display them correctly in the front-end
    formattedDates(bookingCopy) {
      const { booking } = this.props;
      const checkin = get(booking, 'checkin') || bookingCopy?.checkin;
      const checkout = get(booking, 'checkout') || bookingCopy?.checkout;
      const checkinMoment = this.convertToMoment(checkin);
      const checkoutMoment = this.convertToMoment(checkout);
      const sameYear = checkinMoment.format('YYYY') === checkoutMoment.format('YYYY');

      return `${checkinMoment.format(sameYear ? 'MMM DD' : 'MMM DD, YYYY')} - ${checkoutMoment.format('MMM DD, YYYY')}`;
    }

    // Function that calculates the total number of rooms in a booking - to be displayed in the front-end
    totalNumberOfRooms() {
      const { booking } = this.props;
      const summaryByRoomTypes = get(booking, 'summaryByRoomType') || [];
      return summaryByRoomTypes.reduce((totalRooms, type) => {
        totalRooms += type.numberOfRooms;
        return totalRooms;
      }, 0);
    }

    guestSummaryByRoom({ bookingCopy }) {
      const { booking } = this.props;
      const { guests, property, checkin, checkout } = bookingCopy || booking;
      const guestSummaryByRoom = [];
      // get all booking dates in different Room Type.
      const bookingDates = parseDateRangeWithMoment([checkin, checkout]);

      bookingDates.pop();
      guests.forEach(guest => {
        const { roomIndex, id, guestDays } = guest;

        // Find the room or initialize if it doesn't exist
        let roomEntry = guestSummaryByRoom.find(room => room.roomIndex === roomIndex);
        if (!roomEntry) {
          roomEntry = {
            roomIndex,
            guests: [id],
            guestsPerRoom: property.rooms.find(room => room.id === guest.room.id).guestsPerRoom,
            occupancyInDate: bookingDates.reduce((acc, date) => ({ ...acc, [date]: [] }), {}),
            reconciledDate: []
          };
          guestSummaryByRoom.push(roomEntry);
        } else if (!roomEntry.guests.includes(id)) {
          // Ensure uniqueness before pushing
          roomEntry.guests.push(id);
        }

        guestDays.forEach(gD => {
          // Initialize the date array if it doesn't exist and is not reconciled cancelled
          if (
            !roomEntry.occupancyInDate[gD.date] &&
            gD.state !== BookingGuestDayState.ReconciledCancelled &&
            gD.status !== BookingGuestDayStatus.FinalCheckout
          ) {
            roomEntry.occupancyInDate[gD.date] = [];
          }

          if (gD.state !== BookingGuestDayState.ReconciledCancelled && gD.status === BookingGuestDayStatus.Booked) {
            roomEntry.occupancyInDate[gD.date].push(id);
          }

          // Add the date to reconciledDate if it meets the conditions and is not already included
          if (ReconciledPastBookingGuestDayState.includes(gD.state) && !roomEntry.reconciledDate.includes(gD.date)) {
            roomEntry.reconciledDate.push(gD.date);
          }
        });
      });

      return guestSummaryByRoom;
    }

    buildSummary(bookingProp) {
      let booking;
      if (bookingProp) {
        booking = bookingProp;
      } else {
        ({ booking } = this.props);
      }

      const roomCount = get(booking, 'roomCount', 0);
      const bookingGuests = get(booking, 'guests') || [];
      const maxGuest = get(booking, 'maxGuest') || 100;
      const maxDays = get(booking, 'maxDays') || 90;
      const crewSwapOccupancy = get(booking, 'crewSwapOccupancy') || 100;
      return {
        propertyName: get(booking, 'property.name'),
        propertyCity: get(booking, 'property.address.city'),
        propertyRegion: get(booking, 'property.address.region'),
        propertyImg: get(booking, 'property.cardImage.href'),
        guestCount: bookingGuests.length,
        estimatedTotal: `${get(booking, 'estimatedTotal.formatted', 0)} (${get(
          booking,
          'estimatedTotal.name',
          'CAD'
        )})`,
        roomCount,
        maxGuest,
        maxDays,
        crewSwapOccupancy
      };
    }

    noBookingPermissions() {
      const { booking } = this.props;
      const permissions = get(booking, 'permissions') || [];
      const bookingStatus = get(booking, 'bookingStatus');
      const hasBookingStatus = !!bookingStatus;
      return hasBookingStatus && permissions.length === 0;
    }

    hasRequiredPermissions() {
      const { booking } = this.props;
      const currentPermissions = get(booking, 'permissions') || [];
      if (requireAllPermissions && requireAllPermissions.length > 0) {
        const missingPermissions = requireAllPermissions.filter(
          requiredPermission => !currentPermissions.includes(requiredPermission)
        );
        return missingPermissions.length === 0;
      }

      return true;
    }

    verifyRoomTypes() {
      const { booking } = this.props;
      const { summaryByRoomType, property = {} } = booking;
      const { rooms } = property;
      const allRoomsHaveRates = rooms.every(room => room.rate);

      if (allRoomsHaveRates) {
        const roomTypeIdsWithRates = rooms.map(room => (room.rate ? room.id : null)).sort();
        const roomTypeIdsOnBooking = summaryByRoomType.map(roomType => roomType.id).sort();
        const result = roomTypeIdsOnBooking.every(roomTypeId => roomTypeIdsWithRates.includes(roomTypeId));
        return result;
      }

      return true;
    }

    handleMutationError(error) {
      const { router, auth, booking } = this.props;
      const { pushRouteAndScroll } = router;
      const isServerError = get(error, 'isServerError', false);
      const isNetworkError = get(error, 'isNetworkError', false);
      const isNetworkOrServer = isServerError || isNetworkError;
      const code = get(error, 'code');
      const message = get(error, 'message');
      const modMessage = message.includes(':') ? message.split(':')[0] : message;
      error.message = modMessage;

      const organizationSlug = get(auth, 'me.activeOrganization.slug');
      const bookingNumber = get(booking, 'bookingNumber');
      // These are the codes that are very serious and must be addressed
      const seriousCodes = [500, 401, 503, '401', '500', '503'];
      const isSeriousCode = code && seriousCodes.includes(code);
      const isLoginError = modMessage === 'Must be logged in';

      if (errorOnServerErrors && this.mounted && isNetworkOrServer && isSeriousCode && !isLoginError) {
        const currentTime = new Date();
        const timestamp = currentTime.toISOString();
        pushRouteAndScroll('BookingError', {
          timestamp,
          organizationSlug,
          bookingNumber,
          routeName: router.asPath
        });
      }

      return { mutationError: error };
    }

    isBookingLoading() {
      const paymentCodesLoading = get(this.props, 'paymentCodesLoading', false);
      const bookingLoading = get(this.props, 'bookingLoading', false);
      return paymentCodesLoading || bookingLoading;
    }

    isOlderConnectedBooking() {
      const { booking } = this.props;
      // GDS bookings will always have rooms loading if it is older than current day (GDS won't return rates)
      return !this.convertToMoment(booking.checkin).isSameOrBefore(this.convertToMoment().format('YYYY-MM-DD'));
    }

    changeCheckinCheckoutDate = async (bookingId, guestIds, checkinDate, checkoutDate) => {
      const { editChangeCheckinCheckoutDate } = this.props;

      try {
        const {
          data: { changeCheckinCheckoutDateEdit }
        } = await editChangeCheckinCheckoutDate({
          variables: { bookingId, guestIds, checkinDate, checkoutDate }
        });

        if (!changeCheckinCheckoutDateEdit) throw new Error('Missing Response Data');

        return changeCheckinCheckoutDateEdit;
      } catch (error) {
        this.handleMutationError(mutationError(error));
        return { errorMessage: error.message };
      }
    };

    getConfirmedDates = () => {
      const { booking } = this.props;
      const { guests = [] } = booking || {};
      return guests
        .flatMap(guest => guest.guestDays)
        .reduce((acc, { date, state }) => {
          if (
            !date ||
            !state ||
            acc.includes(date) ||
            state === BookingGuestDayState.Unconfirmed ||
            state === BookingGuestDayState.BookingRejected
          )
            return acc;
          acc.push(date);
          return acc;
        }, [])
        .sort();
    };

    getBookingStatus = async () => {
      const { client, intl, booking } = this.props;
      const errorMsg = intl.formatMessage(messages.getBookingStatusError);
      const handleErrorState = (msg = errorMsg, errorCode = 500) =>
        this.asyncSetState({ error: { message: msg, code: errorCode } });

      try {
        const response = await client.query({
          query: QUERY_GET_BOOKING,
          fetchPolicy: 'network-only',
          variables: {
            bookingNumber: booking.bookingNumber,
            draftEdit: isEditingBooking
          }
        });

        return response.data.getBookingByBookingNumber.bookingStatus;
      } catch (error) {
        const { message, code } = mutationError(error);
        handleErrorState(message, code);
        return error;
      }
    };

    render() {
      const { booking, paymentCodes, router, queryError, intl, roomsLoading } = this.props;

      const { error } = this.state;
      const loading = this.isBookingLoading();
      if (loading) return <Placeholder />;

      // if there are any errors then display the error component
      if (error || queryError) {
        const queryErrorMessage = get(queryError, 'message');
        const stateErrorMessage = get(error, 'message');
        const stateErrorCode = get(queryError, 'graphQLErrors[0].data.code', 500);
        const resolvedCode = stateErrorCode === '413' ? null : stateErrorCode;
        const msg = stateErrorMessage || queryErrorMessage;
        return <ErrorPage showRetry statusCode={resolvedCode} statusMessage={msg} />;
      }

      // some page require there to be a booking.
      // render placeholder while its resolving, and if theres no valid booking render the 404 error.
      if (requireBooking && !booking) return <ErrorPage showRetry statusCode={404} />;

      // restrict access to page if client indicates you must have at least one valid permission (cancelled bookings)
      if (!allowEmptyPermissions && this.noBookingPermissions()) {
        return (
          <ErrorPage showRetry statusCode={401} statusMessage={intl.formatMessage(messages.noBookingPermissions)} />
        );
      }

      if (!this.hasRequiredPermissions() && booking.bookingStatus !== BOOKING_STATUS.Pending) {
        return (
          <BookingNoPermission
            policies={this.buildBookingFlowPolicies()}
            routes={{
              gotoRoomAssignments: this.gotoRoomAssignments,
              gotoCostTrackingCodes: this.gotoCostTrackingCodes,
              gotoRoomSchedule: this.gotoRoomSchedule,
              gotoPayment: this.gotoPayment,
              gotoSummary: this.gotoSummary,
              gotoConfirm: this.gotoConfirm
            }}
          />
        );
      }

      if (errorOnInvalidRoomTypes && !this.verifyRoomTypes()) {
        return <ErrorPage statusCode={204} statusMessage={intl.formatMessage(messages.missingRoomRates)} />;
      }

      if (requireBooking && !validateBookingState(booking)) {
        // restrict access to the current page as a result of the status of the booking
        return <ErrorPage showRetry statusCode={999} />;
      }

      // sometimes we don't have direct access to a Payment ID on a booking
      // this could happen if you are supervisor. you should still still the booking Payment ID
      // even thought you don't have direct access to that code.
      const sourceCodes = paymentCodes || [];
      const bookingPaymentCode = get(booking, 'paymentCode', undefined);
      const combinedPaymentCodes = getUnique([...sourceCodes, bookingPaymentCode], 'id');

      // Redirect User to Dashboard if user enters booking number thats not for booking currently in session storage
      const isBookingRoute = checkBookingRoute(router.pathname);
      const bookingNumberInURL = get(router, 'query.bookingNumber', null);
      const isWindowLoadedWithSessionStorage =
        typeof window !== 'undefined' && Object.prototype.hasOwnProperty.call(window.sessionStorage, 'bookingObject');
      if (isWindowLoadedWithSessionStorage && isBookingRoute) {
        const getBookingObject = JSON.parse(sessionStorage.getItem('bookingObject'));
        const { booking: bookingFromSession } = getBookingObject;
        const isBookingNumberSame = bookingFromSession.bookingNumber === bookingNumberInURL;
        if (!isBookingNumberSame) {
          sessionStorage.removeItem('bookingObject');
          return <Redirect route="Dashboard" method="replace" />;
        }
      }

      // build the booking render props
      const bookingRenderProps = {
        booking: {
          ...booking,
          summary: this.buildSummary(),
          formattedDateRange: this.formattedDates(),
          totalNumberOfRooms: this.totalNumberOfRooms(),
          confirmedDates: this.getConfirmedDates(),
          confirmedPastDates: this.getConfirmedDates().filter(date => {
            const today = this.convertToMoment();
            const dateMoment = this.convertToMoment(date);
            return dateMoment.isBefore(today, 'day');
          })
        },
        policies: this.buildBookingFlowPolicies(),
        paymentCodes: combinedPaymentCodes,
        loading,
        bookingNumber: get(router, 'query.bookingNumber'),
        startPropertyBookingFlow: this.startPropertyBookingFlow,
        initiateBooking: this.initiateBooking,
        convertToMoment: this.convertToMoment,
        gotoRoomAssignments: this.gotoRoomAssignments,
        gotoCostTrackingCodes: this.gotoCostTrackingCodes,
        gotoRoomSchedule: this.gotoRoomSchedule,
        gotoPayment: this.gotoPayment,
        gotoSummary: this.gotoSummary,
        gotoConfirm: this.gotoConfirm,
        addCrewMembersToRoom: this.addCrewMembersToRoom,
        editGuestInfo: this.editGuestInfo,
        setCostTrackingCodes: this.setCostTrackingCodes,
        submitBooking: this.submitBooking,
        updateBookingTravelRequest: this.updateBookingTravelRequest,
        updateSpecialRequests: this.updateSpecialRequests,
        getIdentityForGuest: this.getIdentityForGuest,
        calculateBookingPriceFromFullBooking: this.calculateBookingPriceFromFullBooking,
        calculateRoomScheduleBookingEditRates: this.calculateRoomScheduleBookingEditRates,
        calculateRoomScheduleBookingInitialRates: this.calculateRoomScheduleBookingInitialRates,
        cancelBooking: this.cancelBooking,
        startEditBooking: this.startEditBooking,
        handleMutationError: this.handleMutationError,
        roomsLoading: roomsLoading ? this.isOlderConnectedBooking() : roomsLoading,
        getBookingPaymentStatus: this.getBookingPaymentStatus,
        changeCheckinCheckoutDate: this.changeCheckinCheckoutDate,
        createEditedBooking: this.createEditedBooking,
        getBookingObject: this.getBookingObject,
        setBookingObject: this.setBookingObject,
        modifyBookingObject: this.modifyBookingObject,
        updateNewBookingSummary: this.updateNewBookingSummary,
        updateEditedBookingSummary: this.updateEditedBookingSummary,
        recalculateEditedBookingPrice: this.recalculateEditedBookingPrice,
        getBookingStatus: this.getBookingStatus
      };

      // these are added from the query, but aren't really needed, so don't pass them on.
      // mostly other HOC props.
      const blackListProps = [
        // 'client',
        'router',
        'intl',
        'paymentCodesLoading',
        'bookingLoading',
        'booking',
        'reload',
        'queryError',
        'paymentCodes'
      ];
      const passThroughProps = omit(this.props, blackListProps);
      return <BaseComponent booking={bookingRenderProps} paymentCodes={combinedPaymentCodes} {...passThroughProps} />;
    }
  }

  return compose(
    injectIntl,
    withAnalytics(),
    withApollo,
    withRouting,
    graphql(MUTATION_INITIATE_BOOKING, {
      // Triggered in PDP and Property Pane
      name: 'initiateBooking',
      options: () => ({ errorPolicy: 'all' })
    }),
    graphql(MUTATION_ADD_CREW_MEMBERS_TO_BOOKING_ROOM, {
      name: 'addCrewMembersToBookingRoom',
      options: () => ({ errorPolicy: 'all' })
    }),
    graphql(MUTATION_EDIT_BOOKING_GUEST, {
      name: 'editBookingGuest',
      options: () => ({ errorPolicy: 'all' })
    }),
    graphql(MUTATION_SET_COST_TRACKING_CODES_FOR_BOOKING, {
      name: 'setCostTrackingCodesForBooking',
      options: () => ({ errorPolicy: 'all' })
    }),
    graphql(MUTATION_SUBMIT_BOOKING, {
      name: 'submitBooking',
      options: () => ({ errorPolicy: 'all' })
    }),
    graphql(MUTATION_UPDATE_BOOKING_TRAVEL_REQUEST, {
      name: 'updateBookingTravelRequest',
      options: () => ({ errorPolicy: 'all' })
    }),
    graphql(MUTATION_MODIFY_SPECIAL_REQUEST, {
      name: 'updateSpecialRequests',
      options: () => ({ errorPolicy: 'all' })
    }),
    graphql(MUTATION_CALCULATE_BOOKING_PRICE_FROM_FULL_BOOKING, {
      name: 'calculateBookingPriceFromFullBooking',
      options: () => ({ errorPolicy: 'all' })
    }),
    graphql(MUTATION_CALCULATE_RATES_FOR_ROOM_SCHEDULE_EDIT, {
      name: 'calculateRoomScheduleBookingEditRates',
      options: () => ({ errorPolicy: 'all' })
    }),
    graphql(MUTATION_CALCULATE_RATES_FOR_ROOM_SCHEDULE_INITIAL, {
      name: 'calculateRoomScheduleBookingInitialRates',
      options: () => ({ errorPolicy: 'all' })
    }),
    graphql(MUTATION_CANCEL_BOOKING, {
      name: 'cancelBooking',
      options: props => {
        const bookingNumber = get(props, 'router.query.bookingNumber');
        return {
          errorPolicy: 'all',
          refetchQueries: [{ query: QUERY_GET_BOOKING, variables: { bookingNumber, draftEdit: isEditingBooking } }],
          awaitRefetchQueries: true
        };
      }
    }),
    graphql(MUTATION_START_EDIT, {
      name: 'startEditBooking',
      options: () => ({ errorPolicy: 'all' })
    }),
    graphql(MUTATION_CHANGE_CHECKIN_CHECKOUT_DATE, {
      name: 'editChangeCheckinCheckoutDate',
      options: props => {
        const bookingNumber = get(props, 'router.query.bookingNumber');
        return {
          errorPolicy: 'all',
          refetchQueries: [{ query: QUERY_GET_BOOKING, variables: { bookingNumber, draftEdit: isEditingBooking } }],
          awaitRefetchQueries: true
        };
      }
    }),
    graphql(QUERY_GET_USER_PAYMENT_CODES, {
      skip: ({ auth }) => {
        const canViewPaymentCodes = hasOrganizationPermission(auth, ORGANIZATION_PERMISSIONS.VIEW_PAYMENT_CODES);
        if (!canViewPaymentCodes) return true;
        return !requireBooking;
      },
      options: () => ({
        ssr: false,
        partialRefetch: true,
        errorPolicy: 'all'
      }),
      props: response => {
        const paymentCodesLoading = get(response, 'loading', false);
        const paymentCodes = get(response, 'data.getUserPaymentCodes') || [];
        return {
          paymentCodes,
          paymentCodesLoading
        };
      }
    }),
    graphql(QUERY_GET_ORGANIZATION_COST_TRACKING_CODES, {
      skip: ({ auth }) => {
        const canViewCostTrackingCodes = hasOrganizationPermission(
          auth,
          ORGANIZATION_PERMISSIONS.VIEW_COST_TRACKING_CODES
        );
        if (!canViewCostTrackingCodes) return true;
        return !requireBooking;
      },
      options: () => ({
        ssr: false,
        partialRefetch: true,
        errorPolicy: 'all'
      }),
      props: response => {
        const costTrackingCodes = get(response, 'data.getOrganizationCostTrackingCodes') || [];
        return {
          costTrackingCodesFromBooking: costTrackingCodes
        };
      }
    }),
    graphql(QUERY_GET_BOOKING, {
      skip: () => !requireBooking,
      options: props => {
        const bookingNumber = get(props, 'router.query.bookingNumber');
        const draftEdit = get(options, 'editBooking', false);
        return {
          ssr: false,
          partialRefetch: true,
          errorPolicy: 'all',
          variables: { bookingNumber, draftEdit }
        };
      },
      props: response => {
        const booking = get(response, 'data.getBookingByBookingNumber', {});
        const bookingLoading = get(response, 'data.loading', false);
        const error = get(response, 'data.error');
        const refetch = get(response, 'data.refetch');
        const roomsLoading = get(booking, 'property.connected', false);
        let clonedBooking = null;
        if (booking && booking.property) {
          clonedBooking = cloneDeep(booking);
          clonedBooking.checkin = convertUTCDateToPropertyTime({
            dateInUTC: clonedBooking.checkin,
            propertyTimeZone: clonedBooking.property.timeZone
          });
          clonedBooking.checkout = convertUTCDateToPropertyTime({
            dateInUTC: clonedBooking.checkout,
            propertyTimeZone: clonedBooking.property.timeZone
          });

          const convertedGuests = clonedBooking.guests.map(clonedGuest => {
            const modGuestDays = clonedGuest.guestDays.map(day => ({
              ...day,
              date: convertUTCDateToPropertyTime({
                dateInUTC: day.date,
                propertyTimeZone: clonedBooking.property.timeZone
              })
            }));
            clonedGuest.guestDays = modGuestDays;
            return clonedGuest;
          });

          clonedBooking.guests = convertedGuests;

          clonedBooking.summaryByRoomType.forEach(summary => {
            summary.dates = summary.dates.map(day => ({
              ...day,
              date: convertUTCDateToPropertyTime({
                dateInUTC: day.date,
                propertyTimeZone: clonedBooking.property.timeZone
              })
            }));
          });
        }

        return {
          booking: clonedBooking || booking,
          bookingLoading,
          roomsLoading,
          queryError: error,
          reload: refetch
        };
      }
    }),
    graphql(QUERY_PROPERTY_ROOMS, {
      skip: ({ booking }) => {
        const checkin = get(booking, 'checkin');
        const checkout = get(booking, 'checkout');
        const rooms = get(booking, 'property.rooms');
        const connected = get(booking, 'property.connected', false);
        return !checkin || !checkout || (rooms && rooms.length === 0) || !connected || false;
      },
      options: props => {
        // TODO: Currently, we don't have access to the 'this' object or to getBookingObject function. This will need to refactored.
        const { booking } = JSON.parse(sessionStorage.getItem('bookingObject')) ?? { booking: props.booking };
        const { property } = booking;
        let { checkin, checkout } = booking;
        const slug = get(property, 'slug');
        const connected = get(property, 'connected', false);
        const rooms = get(property, 'rooms') || [];
        const propertyTimeZone = get(property, 'timeZone');

        checkin = convertPropertyDateToUTCTime(checkin, propertyTimeZone);
        checkout = convertPropertyDateToUTCTime(checkout, propertyTimeZone);

        return {
          ssr: false,
          partialRefetch: true,
          errorPolicy: 'all',
          variables: {
            slug,
            dates: {
              checkin,
              checkout
            },
            skip: !checkin || !checkout || rooms.length === 0 || !connected || false
          }
        };
      },
      props: ({ data, ownProps: { booking } }) => {
        const roomsData = get(data, 'property', []);
        const { callForPricing } = roomsData;
        // Build a room objects
        const rateRooms = get(data, 'property.rooms', []);
        const property = get(booking, 'property');
        const rooms = get(property, 'rooms', []);
        const propertyTimeZone = get(property, 'timeZone');

        const newRooms = rooms.map(curRoom => {
          const room = rateRooms.find(r => r.id.toString() === curRoom.id.toString());
          if (room) {
            const rate = {
              formatted:
                callForPricing || room.rate === null ? 'PLEASE CALL FOR ADDITIONAL INFORMATION' : room.rate.formatted,
              name: callForPricing || room.rate === null ? '' : room.rate.name,
              value: callForPricing || room.rate === null ? null : room.rate.value,
              // logged in admins still need to be able to book and see original rate values
              adminValues: room.rate,
              callForPricing,
              symbol: room.rate === null ? '$' : room.rate.symbol
            };
            let strikeoutRate = null;
            if (room.strikeoutRate && rate.value && room.strikeoutRate.value > rate.value) {
              strikeoutRate = room.strikeoutRate.formatted;
            }

            if (room.nightlyRates) {
              room.nightlyRates = room.nightlyRates.map(nightlyRate => ({
                ...nightlyRate,
                date: convertUTCDateToPropertyTime({ dateInUTC: nightlyRate.date, propertyTimeZone })
              }));
            }

            return {
              ...curRoom,
              rate,
              strikeoutRate,
              callForPricing: room.callForPricing,
              nightlyRates: room.nightlyRates,
              availability: PropertyAvailability[room.availability]
            };
          }
          return curRoom;
        });
        return {
          roomsLoading: get(data, 'loading', false),
          booking: {
            ...booking,
            property: {
              ...property,
              rooms: newRooms
            }
          }
        };
      }
    })
  )(ComponentWithBooking);
};

export default withBooking;
